import { ViewWidget, Widget } from '@nordbox/core';
import { readSelectAsset, findSelectLayout, selectInDock } from './AssetViewerDockSelect.js';

let _unregister;
let _viewSequence = 0;
/** Данные trigger-а для ленты миниатюр; URL берётся только из уже отрисованной разметки. */
function readCollectionItem(trigger, assetUI) {
    const image = trigger.matches('img') ? trigger : trigger.querySelector('img');
    const title = trigger.dataset.assetTitle?.trim()
        || trigger.getAttribute('aria-label')?.trim()
        || image?.alt.trim()
        || trigger.textContent?.trim()
        || assetUI;
    const thumbnailUrl = trigger.dataset.assetThumbnail?.trim() || image?.currentSrc || image?.src || undefined;
    return { assetUI, title, thumbnailUrl, trigger };
}
/** Одинаковый data-asset-group на странице образует одну упорядоченную коллекцию. */
function readCollection(opener, assetUI) {
    const group = opener.dataset.assetGroup?.trim();
    if (!group)
        return [readCollectionItem(opener, assetUI)];
    const unique = new Map();
    document.querySelectorAll('[data-asset][data-asset-group]').forEach(trigger => {
        if (trigger.dataset.assetGroup?.trim() !== group)
            return;
        const itemAssetUI = trigger.dataset.asset?.trim();
        if (!itemAssetUI)
            return;
        const item = readCollectionItem(trigger, itemAssetUI);
        const previous = unique.get(itemAssetUI);
        if (!previous || (!previous.thumbnailUrl && item.thumbnailUrl))
            unique.set(itemAssetUI, item);
    });
    const items = [...unique.values()];
    return items.some(item => item.assetUI === assetUI) ? items : [readCollectionItem(opener, assetUI)];
}
/**
 * Подключает один обработчик data-asset для обычной и динамической разметки.
 * 2026-09-25: он же обрабатывает data-asset-viewer-select — выбор файла для панели в колонке
 * раскладки [data-av-layout] (AssetViewerDockSelect). Вне такой раскладки выбор открывает
 * самостоятельный Lightbox, как data-asset.
 */
function registerAssetViewerTriggers() {
    if (_unregister)
        return _unregister;
    let active;
    /** Освобождает временный view, в том числе после запоздавшего ответа. */
    const dispose = (request) => {
        request.opener.removeAttribute('aria-busy');
        const root = request.host.querySelector('.im-asset-viewer');
        if (root)
            Widget.getOnByName(root, 'AssetViewer')?.destroy();
        request.view.destroy();
        request.host.remove();
    };
    /** Закрывает текущий просмотрщик перед выбором другого файла. */
    const close = () => {
        const previous = active;
        active = undefined;
        if (previous)
            dispose(previous);
    };
    /** Загружает общий view через штатный транспорт, затем открывает его сцену. */
    const open = async (assetUI, opener, collection) => {
        if (active?.assetUI === assetUI)
            return;
        close();
        const host = document.createElement('div');
        host.className = 'asset-viewer-host';
        document.body.appendChild(host);
        const view = ViewWidget.createInside(host, {
            dataKey: `assetViewer${++_viewSequence}`,
            viewKey: 'storage.vsmodule_web.assetViewerView',
            values: { assetUI },
            deferredLoad: false
        });
        const request = { assetUI, host, view, opener };
        active = request;
        opener.setAttribute('aria-busy', 'true');
        host.addEventListener('asset-viewer:close', () => {
            if (active === request)
                close();
        });
        try {
            const { AssetViewerWidget } = await import('./AssetViewerWidget.js');
            if (active !== request)
                return;
            view.reload(undefined, () => {
                // Ответ от ранее выбранного файла не должен открывать старую сцену.
                if (active !== request || !host.isConnected) {
                    dispose(request);
                    return;
                }
                opener.removeAttribute('aria-busy');
                const root = host.querySelector('.im-asset-viewer');
                if (root) {
                    const widget = AssetViewerWidget.getOrInit(root);
                    widget.setCollection(collection, assetUI);
                    widget.openLightbox(opener);
                }
                else
                    close();
            }, () => {
                if (active === request)
                    close();
            });
        }
        catch (error) {
            if (active === request)
                close();
            console.error('[AssetViewer] Не удалось открыть просмотрщик', error);
        }
    };
    /** Перехватывает только обычное нажатие на явно обозначенный asset. */
    const onClick = (event) => {
        if (event.defaultPrevented || event.button !== 0
            || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
            return;
        const target = event.target instanceof Element ? event.target : null;
        const opener = target?.closest('[data-asset], [data-asset-viewer-select]');
        const assetUI = opener?.hasAttribute('data-asset') ? opener.dataset.asset?.trim() : opener && readSelectAsset(opener);
        if (!opener || !assetUI || opener.matches(':disabled, [aria-disabled="true"]'))
            return;
        event.preventDefault();
        event.stopPropagation();
        const layout = opener.hasAttribute('data-asset-viewer-select') ? findSelectLayout(opener) : null;
        if (layout)
            void selectInDock(layout, assetUI);
        else
            void open(assetUI, opener, readCollection(opener, assetUI));
    };
    // Capture не дает клику одновременно запустить команду строки или переход по ссылке.
    document.addEventListener('click', onClick, true);
    _unregister = () => {
        document.removeEventListener('click', onClick, true);
        close();
        _unregister = undefined;
    };
    return _unregister;
}

export { registerAssetViewerTriggers };
